import { Injectable } from '@angular/core';
import { AppcuesEvent } from '@core/constants/appcues-event';
import { phErrorMessages } from '@core/constants/ph-error-messages';
import { LocalStorageKey } from '@core/enums/local-storage-key.enum';
import { EcAppSettings } from '@core/models/app-settings.model';
import { AppInitService } from '@core/services/app-init.service';
import { AppcuesService } from '@core/services/appcues.service';
import { CartService } from '@core/services/cart.service';
import { GtmService } from '@core/services/gtm.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { selectCart } from '@core/store/cart';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { getErrorMessage } from '@shared/utils/get-error-message';
import { getErrorTitle } from '@shared/utils/get-error-title';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { selectOrderLineById } from '.';
import { AppState } from '..';
import { logOutResetConcluded } from '../auth/auth.actions';
import * as cartActions from './cart.actions';
// eslint-disable-next-line no-unused-vars
@Injectable()
export class CartEffects {
  addToCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.addToCart),
      concatLatestFrom(() => this.store$.select(selectCart)),
      mergeMap(([{ payload }, cart]) => {
        const limit = Number(this.appSettings.ecomOrderTotalLimit);
        if (limit > 0) {
          const itemTotal = payload.quantity * (payload.discountedPrice ?? payload.price);
          const projectedTotal = (cart?.merchandiseTotal ?? 0) + itemTotal;

          if (projectedTotal > limit) {
            return of(
              cartActions.setOrderTotalLimitError({
                payload: {
                  limit: this.appSettings.ecomOrderTotalLimit.toString(),
                  orderLineId: null,
                  errorType: 'orderTotalLimit',
                },
              }),
            );
          }
        }

        return this.cartService
          .addToCart(payload.sku, payload.quantity, payload.quantityLimit)
          .pipe(
            tap((cartResponse) => this.handelCartModified(cartResponse.isModifiedByAutoUpdate)),
            map(() => cartActions.addToCartSuccess({ payload })),
            catchError((error) => {
              if (error?.error?.ErrorType === 'QuantityLimitExceedException') {
                return of(
                  cartActions.setQuantityLimitErrorModal({
                    payload: {
                      limit: error.error.ErrorMessage,
                      orderLineId: error.error.OrderLineId ?? null,
                      errorType: 'quantityLimit',
                    },
                  }),
                );
              }
              return of(cartActions.addToCartFailure({ error }));
            }),
          );
      }),
    ),
  );

  addToCartsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.addToCartSuccess),
      tap(({ payload }) => {
        this.gtmService.addToCart(payload);
        this.appcuesService.track(AppcuesEvent.AddItemToCart);
      }),
      map(() => cartActions.fetchCart()),
    ),
  );

  addToCartFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cartActions.addToCartFailure),
        tap(({ error }) => {
          this.toastr.error(getErrorMessage(error), getErrorTitle(error));
        }),
      ),
    { dispatch: false },
  );

  fetchCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.fetchCart),
      exhaustMap(() =>
        this.cartService.fetchCart().pipe(
          tap((cart) => this.handelCartModified(cart.isModifiedByAutoUpdate)),
          map((cart) => cartActions.fetchCartSuccess({ cart })),
          catchError((error) => of(cartActions.fetchCartFailure({ error }))),
        ),
      ),
    ),
  );

  setMultiplePurchaseLimitRuleItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.setMultiplePurchaseLimitRuleItems),
      map(() => cartActions.fetchCart()),
    ),
  );

  updateOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.updateOrderLineQty),
      concatLatestFrom(({ orderLineId }) => this.store$.select(selectOrderLineById(orderLineId))),
      switchMap(([{ orderLineId, quantity, quantityLimit }, prevLine]) =>
        this.cartService.updateOrderLine(orderLineId, quantity, quantityLimit).pipe(
          tap((cart) => this.handelCartModified(cart.isModifiedByAutoUpdate)),
          map((cart) => cartActions.updateOrderLineQtySuccess({ cart })),
          catchError((error) => {
            if (error?.error?.ErrorType === 'QuantityLimitExceedException') {
              return of(
                cartActions.setQuantityLimitErrorModal({
                  payload: {
                    limit: error.error.ErrorMessage,
                    orderLineId: error.error.OrderLineId ?? null,
                    errorType: 'quantityLimit',
                  },
                }),
                cartActions.revertOrderLineQty({
                  orderLineId,
                  actualQty: prevLine?.quantity,
                }),
              );
            }
            return of(cartActions.updateOrderLineQtyFailure({ error }));
          }),
        ),
      ),
    ),
  );

  updateOrderLineFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cartActions.updateOrderLineQtyFailure),
        tap(({ error }) => {
          const body = error?.error;
          const errorType = body?.ErrorType;
          const entry = errorType ? phErrorMessages[errorType] : undefined;

          let message: string;
          if (entry === undefined) {
            message = $localize`Try again later`;
          } else if (typeof entry === 'function') {
            message = entry(body.ErrorMessage);
          } else {
            message = entry;
          }

          this.toastr.error(message, $localize`Failed to update cart`);
        }),
      ),
    { dispatch: false },
  );

  mergeCarts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.mergeCarts),
      mergeMap(() =>
        this.cartService.mergeCarts().pipe(
          map(() => cartActions.mergeCartsSuccess()),
          catchError((error) => of(cartActions.mergeCartsFailure({ error }))),
        ),
      ),
    ),
  );

  mergeCartsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.mergeCartsSuccess),
      map(() => cartActions.fetchCart()),
    ),
  );

  newCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.newCart),
      exhaustMap(() =>
        this.cartService.newCart().pipe(
          map(() => cartActions.newCartSuccess()),
          catchError((error) => of(cartActions.newCartFailure({ error }))),
        ),
      ),
    ),
  );

  newCartSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.newCartSuccess),
      map(() => cartActions.resetCartState()),
    ),
  );

  newCartFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.newCartFailure),
      map(() => cartActions.resetCartState()),
    ),
  );

  deleteFromCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.deleteFromCart),
      concatLatestFrom(({ orderLineId }) => this.store$.select(selectOrderLineById(orderLineId))),
      tap(([, orderLine]) => this.gtmService.removeFromCart(orderLine)),
      mergeMap(([{ orderLineId }]) =>
        this.cartService.deleteOrderLine(orderLineId).pipe(
          tap((cart) => this.handelCartModified(cart.isModifiedByAutoUpdate)),
          map((cart) => cartActions.deleteFromCartSuccess({ cart })),
          catchError((error) => of(cartActions.deleteFromCartFailure({ error }))),
        ),
      ),
    ),
  );

  deleteFromCartFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cartActions.deleteFromCartFailure),
        tap(({ error }) => {
          this.toastr.error(getErrorMessage(error), getErrorTitle(error));
        }),
      ),
    { dispatch: false },
  );

  fetchProductsRecommendations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.fetchProductsRecommendations),
      mergeMap(({ skus }) =>
        this.cartService.fetchProductsRecommendations(skus).pipe(
          map((res) =>
            cartActions.fetchProductsRecommendationsSuccess({
              recommendations: res.recommendations,
            }),
          ),
          catchError((error) => of(cartActions.fetchProductsRecommendationsFailure(error))),
        ),
      ),
    ),
  );

  fetchProductsRecommendationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cartActions.fetchProductsRecommendationsFailure),
        tap(({ error }) => {
          this.toastr.error(getErrorMessage(error), getErrorTitle(error));
        }),
      ),
    { dispatch: false },
  );

  addSurpriseBuy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.addSurpriseBuy),
      switchMap(() =>
        this.cartService.addSurpriseBuy().pipe(
          tap((cart) => this.handelCartModified(cart.isModifiedByAutoUpdate)),
          map((cart) => cartActions.addSurpriseBuySuccess({ cart })),
          catchError((error) => of(cartActions.addSurpriseBuyFailure({ error }))),
        ),
      ),
    ),
  );

  resetCartState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.resetCartState),
      map(() => logOutResetConcluded({ state: 'cartState' })),
    ),
  );

  revertOrderLineOnQuantityLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.updateOrderLineQtyFailure),
      filter(({ error }) => error?.error?.ErrorType === 'QuantityLimitExceedException'),
      map(({ error }) =>
        cartActions.revertOrderLineQty({
          orderLineId: error.error.OrderLineId,
          actualQty: error.error.ActualCurrentQty,
        }),
      ),
    ),
  );

  checkOrderTotalLimitOnFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.fetchCartSuccess),
      filter(({ cart }) => {
        const limit = Number(this.appSettings.ecomOrderTotalLimit);
        return limit > 0 && cart?.merchandiseTotal > limit;
      }),
      map(() =>
        cartActions.setOrderTotalLimitError({
          payload: {
            limit: this.appSettings.ecomOrderTotalLimit.toString(),
            orderLineId: null,
            errorType: 'orderTotalLimit',
          },
        }),
      ),
    ),
  );

  checkOrderTotalLimitOnUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.updateOrderLineQtySuccess),
      concatLatestFrom(() => this.store$.select(selectCart)),
      filter(([, cart]) => {
        const limit = Number(this.appSettings.ecomOrderTotalLimit);
        return limit > 0 && cart?.merchandiseTotal > limit;
      }),
      map(() =>
        cartActions.setOrderTotalLimitError({
          payload: {
            limit: this.appSettings.ecomOrderTotalLimit.toString(),
            orderLineId: null,
            errorType: 'orderTotalLimit',
          },
        }),
      ),
    ),
  );

  private appSettings: EcAppSettings;

  private handelCartModified(isCartModified): void {
    if (isCartModified) {
      this.localStorageService.setItem(LocalStorageKey.isCartUpdated, 'true');
    }
  }

  constructor(
    private store$: Store<AppState>,
    private actions$: Actions,
    private cartService: CartService,
    private toastr: ToastrService,
    private gtmService: GtmService,
    private appcuesService: AppcuesService,
    private localStorageService: LocalStorageService,
    appInitService: AppInitService,
  ) {
    this.appSettings = appInitService.Settings.ec;
  }
}
